// PasswordResetModal.jsx - Reusable password reset modal component
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FONT_SIZE, TOUCH } from '@/core/platform/platformStandards';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  useTheme,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon
} from '@mui/icons-material';

const PasswordResetModal = ({ 
  open, 
  onClose, 
  onSubmit, 
  loading = false, 
  error = '', 
  title = 'Reset Password',
  submitButtonText = 'Reset Password'
}) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Password validation state - matching registration
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasUpperCase: false
  });

  // Real-time password validation - matching registration
  const validatePassword = (password) => {
    const validation = {
      minLength: password.length >= 8,
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[@$!%*?&]/.test(password),
      hasUpperCase: /[A-Z]/.test(password)
    };
    setPasswordValidation(validation);
    return validation;
  };

  // Password validation criteria - matching registration
  const passwordCriteria = [
    { key: 'minLength', label: 'At least 8 characters' },
    { key: 'hasUpperCase', label: 'One uppercase letter' },
    { key: 'hasNumber', label: 'One number' },
    { key: 'hasSpecialChar', label: 'One special character' }
  ];

  const validateForm = () => {
    const { newPassword, confirmPassword } = formData;

    if (!newPassword) {
      return 'New password is required';
    }

    // Check all password criteria
    const passwordCheck = validatePassword(newPassword);
    if (!Object.values(passwordCheck).every(Boolean)) {
      return 'Password must meet all requirements';
    }

    if (!confirmPassword) {
      return 'Please confirm your password';
    }

    if (newPassword !== confirmPassword) {
      return 'Passwords do not match';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      return;
    }

    // Call parent onSubmit with the new password
    await onSubmit(formData.newPassword);
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Validate password in real-time
    if (field === 'newPassword') {
      validatePassword(value);
    }
  };

  const handleTogglePasswordVisibility = (field) => () => {
    if (field === 'newPassword') {
      setShowPassword(!showPassword);
    } else if (field === 'confirmPassword') {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({ newPassword: '', confirmPassword: '' });
    setPasswordValidation({
      minLength: false,
      hasNumber: false,
      hasSpecialChar: false,
      hasUpperCase: false
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  const isFormValid = Object.values(passwordValidation).every(Boolean) && 
                     formData.newPassword === formData.confirmPassword &&
                     formData.confirmPassword.length > 0;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      fullScreen={isNarrow}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.paper,
          border: isNarrow ? 'none' : `2px solid ${theme.palette.primary.main}`,
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            value={formData.newPassword}
            onChange={handleInputChange('newPassword')}
            fullWidth
            margin="dense"
            autoComplete="new-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon
                    // intentional: adornment icon sized above theme default for visual balance with input text
                    sx={{ color: theme.palette.text.secondary, fontSize: FONT_SIZE.xl }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility('newPassword')}
                    edge="end"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    sx={{ minWidth: TOUCH.minSize, minHeight: TOUCH.minSize }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                minHeight: TOUCH.minSize,
                fontSize: FONT_SIZE.sm,
              }
            }}
          />

          {/* Password Requirements - Always visible, positioned under password field */}
          <Box sx={{ mb: 2 }}>
            <Grid container>
              {passwordCriteria.map((criterion) => (
 <Grid size={{ xs: 12, sm: 6 }} key={criterion.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {passwordValidation[criterion.key] ? (
                      <CheckCircleIcon 
                        sx={{ 
                          // intentional: compact password criterion icon sized to match caption text
                          fontSize: 12, 
                          color: 'success.main',
                          mr: 0.5,
                          transition: 'all 0.2s ease'
                        }} 
                      />
                    ) : (
                      <RadioButtonUncheckedIcon 
                        sx={{ 
                          // intentional: compact password criterion icon sized to match caption text
                          fontSize: 12, 
                          color: 'text.disabled',
                          mr: 0.5,
                          transition: 'all 0.2s ease'
                        }} 
                      />
                    )}
                    <Typography 
                      variant="caption"
                      sx={{
                        color: passwordValidation[criterion.key] ? 'success.main' : 'text.secondary',
                        fontWeight: passwordValidation[criterion.key] ? 500 : 400,
                        transition: 'all 0.2s ease',
                        // intentional: sub-token size for compact password criteria list
                        fontSize: '0.65rem'
                      }}
                    >
                      {criterion.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <TextField
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            fullWidth
            margin="dense"
            autoComplete="new-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon
                    // intentional: adornment icon sized above theme default for visual balance with input text
                    sx={{ color: theme.palette.text.secondary, fontSize: FONT_SIZE.xl }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility('confirmPassword')}
                    edge="end"
                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                    sx={{ minWidth: TOUCH.minSize, minHeight: TOUCH.minSize }}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                minHeight: TOUCH.minSize,
                fontSize: FONT_SIZE.sm,
              }
            }}
          />
        </form>
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 2, sm: 3 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: 'stretch',
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button 
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          fullWidth={isNarrow}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !isFormValid}
          fullWidth={isNarrow}
          sx={{ 
            ml: { xs: 0, sm: 2 },
            minHeight: { xs: TOUCH.minSize, sm: TOUCH.comfySize },
            fontSize: FONT_SIZE.sm,
          }}
        >
          {loading ? (
            <CircularProgress size={TOUCH.iconSize} color="inherit" />
          ) : (
            submitButtonText
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PasswordResetModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  title: PropTypes.string,
  submitButtonText: PropTypes.string
};

export default PasswordResetModal;
